import Vue from 'vue';
import Moment from 'moment-timezone';
import { cloneDeep, uniq, omit } from 'lodash';
import { i18n } from '@/plugins/i18n';

import { compileAsyncMutations } from '@/utils/async';
import {
  GWO_DATE_STORAGE_KEYNAME,
  DEFAULT_5W1H_HINTS,
  LSK_HIDE_ALL_RELEASE_ESSENTIAL,
  LSK_HIDE_NEWEST_RELEASE_ESSENTIAL,
  LSK_DATA_USAGE_DISPLAYED,
} from '@/utils/constants';
import { parseWorkingDays } from '@/utils/main';
import SPage from '@/utils/classes/SPage';
import * as types from './mutation-types';


/**
 * Set the proper problem hints from companyProblemHints and customProblemHints
 * @param  {Object} state - store state
 * @param  {Object} teamObject - currentTeam object as stored in the state
 */
function updateTeamPbHints(state, teamObject) {
  if (!teamObject || !teamObject.companyProblemHints || !teamObject.customProblemHints) {
    return;
  }
  const { companyProblemHints = {}, customProblemHints = {} } = teamObject;
  const hints = { ...companyProblemHints, ...customProblemHints };
  // Merge team and company impact hints by giving priority to team parameters
  hints.impact = cloneDeep(customProblemHints.impact || []);
  (companyProblemHints.impact || []).forEach((impactHint) => {
    if (!hints.impact.some(iHint => iHint.impact === impactHint.impact)) {
      hints.impact.push(impactHint);
    }
  });
  DEFAULT_5W1H_HINTS.forEach(({ key, help }) => {
    if (hints[key] === undefined || hints[key] === '' || /^\s*$/.test(hints[key])) {
      hints[key] = i18n.t(help);
    }
  });
  const inProblemSheet = teamObject.customHintsProblemSheet === 1
    || (teamObject.companyHintsProblemSheet === 1 && teamObject.customHintsProblemSheet === 2);
  Vue.set(state.currentTeam, 'teamProblemHints', hints);
  Vue.set(state.currentTeam, 'teamHintsProblemSheet', inProblemSheet);
}

// Will be default export
const mutations = {

  [types.SET_ATTEMPTING_LOGIN](state, isAttempting) {
    state.attemptingLogin = isAttempting === true;
  },

  [types.SET_CURRENT_JWT](state, payload) {
    if (payload) {
      state.jwt = payload;
    }
  },

  [types.SET_CURRENT_USER](state, payload) {
    state.currentUser = payload;
    if (payload && payload.id) {
      if (!window.localStorage.getItem(`${LSK_DATA_USAGE_DISPLAYED}${payload.id}`)) {
        state.displayDataUsage = true;
      }
    }
  },

  [types.SET_CURRENT_USER_PARAM](state, payload) {
    const allowedKeys = ['email', 'username', 'firstName', 'lastName'];
    if (state.currentUser && payload.users instanceof Array) {
      payload.users.forEach((user) => {
        if (state.currentUser.id === user.id) {
          Object.keys(user).forEach((key) => {
            if (allowedKeys.includes(key)) {
              state.currentUser[key] = user[key];
            }
          });
        }
      });
    }
  },

  [types.SET_CURRENT_TEAM](state, payload) {
    if (payload && payload.workingDays) {
      payload.workingDays = parseWorkingDays(payload.workingDays);
    }
    // Check defaultHomePage has the right format
    if (payload) {
      const { defaultHomePage } = payload;
      payload.defaultHomePage = defaultHomePage && defaultHomePage.pageId
        ? defaultHomePage
        : { pageId: SPage.DASHBOARD.id };
    }
    state.currentTeam = payload;
    updateTeamPbHints(state, state.currentTeam);
  },

  [types.SET_CURRENT_TEAM_PARAM](state, { payload, response: { data } }) {
    if (payload && data) {
      Object.keys(payload).forEach((key) => {
        if (typeof data[key] !== 'undefined') {
          state.currentTeam[key] = data[key];
        }
      });
    }
    updateTeamPbHints(state, state.currentTeam);
  },

  [types.HIDE_DATA_USAGE](state) {
    if (state.currentUser && state.currentUser.id) {
      window.localStorage.setItem(`${LSK_DATA_USAGE_DISPLAYED}${state.currentUser.id}`, true);
      state.displayDataUsage = false;
    }
  },

  [types.EXPAND_SETTINGS_ITEM](state, payload) {
    state.expandedSettings[payload.id] = payload.expanded;
  },

  [types.SET_INDICATOR](state, payload) {
    let { indicator } = payload;
    const { range } = payload;
    const prev = state.indicators[`${indicator.id}`];
    indicator = { ...omit(prev, ['inputValues', 'objectives']), ...indicator };
    let mStart;
    let mEnd;
    if (range) {
      mStart = Moment(range.start);
      mEnd = Moment(range.end);
    }
    if (prev) {
      // Keep old values if not overriding new ones
      const newValKeys = [];
      if (mStart && mEnd) {
        for (let i = 0, end = mEnd.diff(mStart, 'days') + 1; i < end; i++) {
          const dKey = mStart.format('YYYY-MM-DD');
          newValKeys.push(dKey);
          if (prev.inputValues && prev.inputValues[dKey] && !indicator.inputValues[dKey]) {
            delete prev.inputValues[dKey];
          }
          mStart.add(1, 'days');
        }
      }

      // Replaced Object.assign with spread as Object.assign was 20x slower
      indicator.inputValues = { ...prev.inputValues || {}, ...indicator.inputValues || {} };
      // Same for objectives
      indicator.objectives = { ...prev.objectives || {}, ...indicator.objectives || {} };
    }
    Object.freeze(indicator);
    Vue.set(state.indicators, `${indicator.id}`, indicator);
  },

  [types.SET_INDICATOR_ANALYSIS_MSG](state, msg) {
    state.indicatorAnalysisMsg = msg;
  },

  [types.SET_CUMULATED_INDICATORS](state, payload) {
    const allIndics = payload.indicators || [];
    if (payload.themes instanceof Array) {
      payload.themes.forEach((theme) => {
        theme.indicators.forEach((indic) => {
          indic.indicators = allIndics.filter(i => i.libraryId === indic.id);
          indic.teams = indic.indicators.map(i => i.team);
          const prev = state.cumulatedIndicators[`${indic.id}`];
          if (prev) {
            // Keep old values if not overriding new ones
            indic.inputValues = { ...prev.inputValues || {}, ...indic.inputValues || {} };
            // Same for objectives
            indic.objectives = { ...prev.objectives || {}, ...indic.objectives || {} };
          }
          Object.freeze(indic);
          Vue.set(state.cumulatedIndicators, `${indic.id}`, indic);
        });
      });
    }
  },

  /**
   * Add an array of themes of a team to the state
   */
  [types.SET_TEAM_THEMES](state, payload) {
    if (payload instanceof Array) {
      const teams = uniq(payload.map(theme => theme.team));
      teams.forEach((teamId) => {
        Vue.set(state.indicatorsThemesPerTeam, `${teamId}`, []);
      });
      payload.forEach((theme) => {
        state.indicatorsThemesPerTeam[`${theme.team}`].push(theme);
      });
    }
  },

  [types.SET_INDICATORS_ARRAY](state, payload) {
    const team = payload.team || state.currentTeam.id;
    Vue.set(state.indicatorsArrayPerTeam[`${team}`], 'data', payload.indicators);
  },

  [types.SET_INDICATORS_ARRAY_LOADING](state, payload) {
    const team = payload.team || (state.currentTeam && state.currentTeam.id);
    if (!team) {
      return;
    }
    const isLoading = payload.loading === true;
    if (state.indicatorsArrayPerTeam[`${team}`]) {
      state.indicatorsArrayPerTeam[`${team}`].loading = isLoading;
    } else {
      Vue.set(state.indicatorsArrayPerTeam, `${team}`, { loading: isLoading });
    }
  },

  [types.SET_INDICATOR_WARNINGS](state, payload) {
    Object.keys(payload).forEach((indicator) => {
      Vue.set(state.indicatorWarnings, `${indicator}`, payload[`${indicator}`]);
    });
  },

  /**
   * Only used when offlineMode is on.
   * Set a value for an indicator at a specific date.
   */
  [types.SET_INDICATOR_VALUE](state, payload) {
    const prev = cloneDeep(state.indicators[`${payload.indicatorId}`]);
    prev.inputValues = prev.inputValues || {};
    prev.inputValues[payload.date] = prev.inputValues[payload.date] || {};
    prev.inputValues[payload.date].reported = payload.date;
    prev.inputValues[payload.date].value = payload.value;
    Vue.set(state.indicators, `${payload.indicatorId}`, prev);
  },

  [types.SET_ACTIVE_ROUTE_CLICKED](state, route) {
    state.activeRouteClicked = route;
    state.activeRouteMatched = null;
  },

  [types.SET_ACTIVE_ROUTE_MATCHED](state, route) {
    state.activeRouteMatched = route;
  },

  [types.SET_USER_DATA_LODAING](state, payload) {
    if (!payload.id) return;
    const isLoading = payload.loading === true;
    if (state.userData[`${payload.id}`]) {
      state.userData[`${payload.id}`].loading = isLoading;
    } else {
      Vue.set(state.userData, `${payload.id}`, { loading: isLoading });
    }
  },

  [types.SET_USER_DATA](state, payload) {
    if (!payload.id) return;
    Vue.set(state.userData[`${payload.id}`], 'user', payload);
    state.userData[`${payload.id}`].loading = false;
  },

  [types.SET_USER_PICTURE_LOADING](state, payload) {
    if (payload.id) {
      const isLoading = payload.loading === true;
      if (state.userData[`${payload.id}`]) {
        state.userData[`${payload.id}`].pictureLoading = isLoading;
      } else {
        Vue.set(state.userData, `${payload.id}`, { pictureLoading: isLoading });
      }
    }
  },

  [types.SET_USER_PICTURE](state, payload) {
    if (payload.id) {
      Vue.set(state.userData[`${payload.id}`], 'pictureData', payload.data || '');
      state.userData[`${payload.id}`].pictureLoading = false;
    }
  },

  [types.SET_INFO_DATA_LOADING](state, payload) {
    if (payload.date) {
      const isLoading = payload.loading === true;
      if (state.infoAtDate[`${payload.date}`]) {
        state.infoAtDate[`${payload.date}`].loading = isLoading;
      } else {
        Vue.set(state.infoAtDate, `${payload.date}`, {
          loading: isLoading,
          infos: [],
          sentInfos: [],
        });
      }
    }
  },

  [types.SET_INFO_DATA](state, payload) {
    const keys = ['infos', 'sentInfos'];
    if (payload.data) {
      if (payload.date) {
        state.infoAtDate[`${payload.date}`].infos = payload.data.infos;
        state.infoAtDate[`${payload.date}`].sentInfos = payload.data.sentInfos;
        state.infoAtDate[`${payload.date}`].loading = false;
      }

      // Mak information accessible per ID
      keys.forEach((key) => {
        (payload.data[key] || []).forEach((info) => {
          Vue.set(state.infoPerId, `${info.id}`, info);
        });
      });

      if (payload.id && state.infoPerId[`${payload.id}`]) {
        // A specific info was updated : update the state if it's in it
        const allInfos = cloneDeep(state[types.GET_INFOS.stateKey] || { infos: [], sentInfos: [] });
        keys.forEach((key) => {
          const foundInfo = (allInfos[key] || []).findIndex(info => info.id === payload.id);
          if (foundInfo >= 0) {
            allInfos[key][foundInfo] = state.infoPerId[`${payload.id}`];
          } else {
            allInfos[key].push(state.infoPerId[`${payload.id}`]);
          }
        });
        Vue.set(state, types.GET_INFOS.stateKey, allInfos);
      }
    }
  },

  [types.INIT_HIDDEN_ANNOUNCEMENTS](state) {
    const hiddenItems = localStorage.getItem('hiddenShizenAnnouncements');
    if (hiddenItems && hiddenItems.length) {
      state.hiddenAnnouncements = hiddenItems.split(',').map(id => Number(id));
    }
  },

  [types.HIDE_ANNOUNCEMENT](state, payload) {
    if (payload.id) {
      state.hiddenAnnouncements.push(Number(payload.id));
      localStorage.setItem(
        'hiddenShizenAnnouncements',
        state.hiddenAnnouncements.join(','),
      );
    }
  },

  [types.SHOW_ALL_ANNOUNCEMENTS](state) {
    state.hiddenAnnouncements = [];
    localStorage.removeItem('hiddenShizenAnnouncements');
  },

  // Release notes
  // Don't show again for the current version
  [types.SET_HIDE_RELEASE_ESSENTIAL](state, hide) {
    const lsk = `${LSK_HIDE_NEWEST_RELEASE_ESSENTIAL}${state.version}`;
    if (hide) {
      window.localStorage.setItem(lsk, 'true');
    } else {
      window.localStorage.removeItem(lsk);
    }
    state.hideReleaseEssential = !!hide;
  },
  // Don't show again for any version
  [types.SET_HIDE_ALL_RELEASE_ESSENTIAL](state, hide) {
    const currentLsk = `${LSK_HIDE_NEWEST_RELEASE_ESSENTIAL}${state.version}`;
    const lsk = LSK_HIDE_ALL_RELEASE_ESSENTIAL;
    if (hide) {
      window.localStorage.setItem(lsk, 'true');
      state.hideReleaseEssential = true;
    } else {
      window.localStorage.removeItem(lsk);
      state.hideReleaseEssential = window.localStorage.getItem(currentLsk) === 'true';
    }
    state.hideAllReleaseEssential = !!hide;
  },

  /** */
  [types.INCREASE_EDITION_MODAL_INDEX](state, queryParam) {
    const indexes = Object.values(state.editionModalsZIndexes);
    const maxIndex = indexes.length === 0 ? 0 : Math.max(...indexes);
    Vue.set(state.editionModalsZIndexes, queryParam, maxIndex + 1);
  },

  /** */
  [types.RESET_EDITION_MODAL_INDEX](state, queryParam) {
    Vue.delete(state.editionModalsZIndexes, queryParam);
  },

  // ====================
  // Cfg pages related mutations
  [types.SET_CFG_PAGE](state, payload) {
    Vue.set(state.cfgPages, `${payload.id}`, payload);
    (payload.widgets || []).forEach((w) => {
      Vue.set(state.cfgPagesWidgets, `${w.id}`, w);
    });
  },

  [types.SET_CFG_PAGE_WIDGET](state, payload) {
    let widgetExists = false;
    Vue.set(state.cfgPagesWidgets, `${payload.id}`, payload);
    // Search and replace (or push) in cfgPages
    const page = state.cfgPages[`${payload.page}`];
    for (let i = 0, len = page.widgets.length; i < len; i++) {
      if (page.widgets[i].id === payload.id) {
        page.widgets.splice(i, 1, payload);
        widgetExists = true;
      }
    }
    if (!widgetExists) {
      // Widget not found in page's widgets array => new widget
      page.widgets.push(payload);
    }
    // Set ordered zIndexes to each widget
    const { length } = page.widgets;
    let itIndex = 1;
    page.widgets
      .sort((w1, w2) => w1.zIndex - w2.zIndex)
      .forEach((widget) => {
        if (widget.id === payload.id) {
          widget.zIndex = length;
          state.cfgPagesWidgets[`${payload.id}`].zIndex = length;
        } else {
          widget.zIndex = itIndex++;
        }
      });
  },

  [types.SET_TEXT_PER_DATE_WIDGET](state, payload) {
    if (state.cfgPagesWidgets[`${payload.widget}`]) {
      const widget = state.cfgPagesWidgets[`${payload.widget}`];
      if (!widget.textPerDate) {
        widget.textPerDate = { [payload.date]: payload };
      } else {
        widget.textPerDate[payload.date] = payload;
      }
      Vue.set(state.cfgPagesWidgets, `${payload.widget}`, widget);
    }
  },


  // ====================
  // GW related mutations
  [types.SET_GW_OVERLAY_VISIBILITY](state, flag) {
    state.gwOverlayVisibility = !!flag;
  },

  [types.SET_GW_OVERLAY_LAST_DATE](state, payload) {
    const moment = Vue.moment(payload);
    if (!moment.isValid()) {
      return;
    }
    state.gwOverlayLastDate = moment;
    localStorage.setItem(GWO_DATE_STORAGE_KEYNAME, moment.format());
  },

};

// For each async mutations, dynamically add three mutations
// See @/utils/async for more details
compileAsyncMutations(mutations, types);

export default mutations;
